/**
 * Setbacks.js
 * 
 * This module handles the creation and manipulation of setbacks in architectural models.
 * Setbacks are inward offsets from the boundary of a polygon, commonly used in 
 * architectural design to represent building restrictions, roof overhangs, or margins.
 * 
 * The module provides functionality to:
 * - Add and adjust setbacks to polygon edges
 * - Calculate proper inward normals for setback direction
 * - Handle intersection adjustments between connected setback edges
 * - Update the visual representation of setbacks
 */

import { POLYGONS } from "../../Global/variables";
import { createEdgesWireframe, createSegmentPlane } from "../../Global/functions";
import { sceneManager } from "../../SetUps/SceneManager";
import { MATERIALS } from "../../Global/materials";
import * as THREE from "three";
import { removeMesh } from "./Functions";

/**
 * Adds or adjusts a setback to a specific edge of a polygon
 * 
 * This function calculates new positions for the edge's vertices by moving them
 * inward according to the specified setback distance. It also handles the
 * adjustment of connected edges to maintain a continuous setback perimeter.
 * 
 * IMPORTANT: The setback is always computed from the ORIGINAL polygon edge,
 * not incrementally from the last setback position. This guarantees that
 * setbacks never drift outside the polygon when values are increased/decreased.
 * 
 * @param {Array} setbacks - Array of setback edges for the polygon
 * @param {number} edgeIndex - Index of the edge to apply the setback to
 * @param {number} inputValue - Setback distance in inches
 * @param {number} segmentIndex - Index of the segment/polygon in the POLYGONS array
 */
export function addSetbackToEdge(setbacks, edgeIndex, inputValue, segmentIndex) {
    // Create a plane representing the segment/polygon
    const plane = createSegmentPlane(segmentIndex);

    // Polygon mesh + original edge data (this is the TRUE boundary)
    const segment = POLYGONS[segmentIndex].shape;
    const originalEdge = POLYGONS[segmentIndex].edges[edgeIndex];

    // Setback object for this edge
    const setback = setbacks[edgeIndex];

    // Original edge vertices (always on the polygon boundary)
    const basePoint1 = originalEdge.point1;
    const basePoint2 = originalEdge.point2;

    // Convert input setback (inches) to meters; clamp to >= 0 just in case
    const setbackDistance = Math.max(0, inputValue) * 0.0254;

    // Calculate the normal vector using the ORIGINAL edge, not the offset one
    const segNormal = plane.normal.clone().normalize();
    const normal = calculateNormal(basePoint1, basePoint2, segNormal);

    // Midpoint of the ORIGINAL edge
    const midpoint = new THREE.Vector3()
        .addVectors(basePoint1, basePoint2)
        .divideScalar(2);

    // Ensure the normal points inward (inside the polygon)
    const inwardNormal = ensureInwardNormal(normal, midpoint, segNormal, segment);

    // NEW setback vertices are always: original edge + inwardNormal * distance
    const newVertex1 = basePoint1.clone().add(
        inwardNormal.clone().multiplyScalar(setbackDistance)
    );
    const newVertex2 = basePoint2.clone().add(
        inwardNormal.clone().multiplyScalar(setbackDistance)
    );

    // Adjust connected edges to maintain a continuous perimeter
    adjustConnectedEdges(setbacks, edgeIndex, newVertex1, newVertex2, setback);

    // Store logical value in inches (for UI, etc.)
    setback.value = inputValue;

    // Update the visual representation of the setback
    updateWireframe(setbacks, segmentIndex);
}

/**
 * Calculates the normal vector to an edge in 3D space
 * 
 * The normal is perpendicular to both the edge and the segment normal,
 * which ensures it points along the plane of the polygon.
 * 
 * @param {THREE.Vector3} v1 - First vertex of the edge
 * @param {THREE.Vector3} v2 - Second vertex of the edge
 * @param {THREE.Vector3} segNormal - Normal vector of the segment/polygon plane
 * @returns {THREE.Vector3} Normalized normal vector
 */
function calculateNormal(v1, v2, segNormal) {
    // Calculate the edge direction vector
    const edge = new THREE.Vector3().subVectors(v2, v1);
    
    // Cross product gives a vector perpendicular to both the edge and segment normal
    const normal = new THREE.Vector3().crossVectors(edge, segNormal);
    
    // Return the normalized normal vector
    return normal.normalize();
}

/**
 * Ensures the normal vector points inward (inside the polygon)
 * 
 * This function uses raycasting to determine if the normal is pointing inside or outside
 * the polygon, and flips it if necessary to ensure it points inward.
 * 
 * @param {THREE.Vector3} normal - The normal vector to check and potentially flip
 * @param {THREE.Vector3} midpoint - The midpoint of the edge
 * @param {THREE.Vector3} direction - The direction vector for raycasting (segment normal)
 * @param {THREE.Mesh} segment - The polygon mesh for intersection testing
 * @returns {THREE.Vector3} The normal vector, guaranteed to point inward
 */
function ensureInwardNormal(normal, midpoint, direction, segment) {
    const raycaster = new THREE.Raycaster();
    
    // Create a point offset from the midpoint in the normal direction
    const newPoint = new THREE.Vector3().addVectors(midpoint, normal);
    
    // Cast a ray in the direction opposite to the segment normal
    raycaster.set(
        newPoint.clone().add(direction.clone().multiplyScalar(5)),
        direction.clone().negate()
    );
    let intersect = raycaster.intersectObject(segment, false);
    
    // Cast a ray in the direction of the segment normal
    raycaster.set(
        newPoint.clone().add(direction.clone().negate().multiplyScalar(5)),
        direction.clone()
    );
    let intersect2 = raycaster.intersectObject(segment, false);
    
    // If we get intersections, the normal is pointing inward
    if (intersect.length + intersect2.length > 0) {
        return normal;
    } else {
        // Otherwise, flip the normal to point inward
        return normal.clone().negate();
    }
}

/**
 * Adjusts connected edges when a setback is modified
 * 
 * This function ensures that when one edge's setback is modified, the connected
 * edges are adjusted to maintain a continuous perimeter without gaps or overlaps.
 * It handles different connection configurations based on shared vertex IDs.
 * 
 * @param {Array} setbacks - Array of setback edges
 * @param {number} edgeIndex - Index of the edge being modified
 * @param {THREE.Vector3} newVertex1 - New position for the first vertex
 * @param {THREE.Vector3} newVertex2 - New position for the second vertex
 * @param {Object} edge - The edge being modified
 */
function adjustConnectedEdges(setbacks, edgeIndex, newVertex1, newVertex2, edge) {
    // Get indices of the previous and next edges (wrapping around at ends)
    const prevEdgeIndex = (edgeIndex - 1 + setbacks.length) % setbacks.length;
    const nextEdgeIndex = (edgeIndex + 1) % setbacks.length;

    // Get references to the previous and next edges
    const prevEdge = setbacks[prevEdgeIndex];
    const nextEdge = setbacks[nextEdgeIndex];

    // Adjust the previous edge if it exists
    if (prevEdge) {
        // Check different connection scenarios based on shared vertex IDs
        if (prevEdge.vertexId2 === edge.vertexId1) {
            // Previous edge's end connects to current edge's start
            let nearestPoint = getNearestPointOnLineSegment(prevEdge.point1, prevEdge.point2, newVertex1, newVertex2);
            newVertex1.copy(nearestPoint);
            prevEdge.point2 = newVertex1;
        } 
        else if (prevEdge.vertexId1 === edge.vertexId2) {
            // Previous edge's start connects to current edge's end
            let nearestPoint = getNearestPointOnLineSegment(prevEdge.point2, prevEdge.point1, newVertex2, newVertex1);
            newVertex2.copy(nearestPoint);
            prevEdge.point1 = newVertex2;
        }
        else if (prevEdge.vertexId1 === edge.vertexId1) {
            // Previous edge's start connects to current edge's start
            let nearestPoint = getNearestPointOnLineSegment(prevEdge.point2, prevEdge.point1, newVertex1, newVertex2);
            newVertex1.copy(nearestPoint);
            prevEdge.point1 = newVertex1;
        }
        else if (prevEdge.vertexId2 === edge.vertexId2) {
            // Previous edge's end connects to current edge's end
            let nearestPoint = getNearestPointOnLineSegment(prevEdge.point1, prevEdge.point2, newVertex2, newVertex1);
            newVertex2.copy(nearestPoint);
            prevEdge.point2 = newVertex2;
        }
    }

    // Adjust the next edge if it exists, following the same pattern as above
    if (nextEdge) {
        if (nextEdge.vertexId1 === edge.vertexId2) {
            let nearestPoint = getNearestPointOnLineSegment(nextEdge.point2, nextEdge.point1, newVertex2, newVertex1);
            newVertex2.copy(nearestPoint);
            nextEdge.point1 = newVertex2;
        }
        else if (nextEdge.vertexId2 === edge.vertexId1) {
            let nearestPoint = getNearestPointOnLineSegment(nextEdge.point1, nextEdge.point2, newVertex1, newVertex2);
            newVertex1.copy(nearestPoint);
            nextEdge.point2 = newVertex1;
        }
        else if (nextEdge.vertexId1 === edge.vertexId1) {
            let nearestPoint = getNearestPointOnLineSegment(nextEdge.point2, nextEdge.point1, newVertex1, newVertex2);
            newVertex1.copy(nearestPoint);
            nextEdge.point1 = newVertex1;
        }
        else if (nextEdge.vertexId2 === edge.vertexId2) {
            let nearestPoint = getNearestPointOnLineSegment(nextEdge.point1, nextEdge.point2, newVertex2, newVertex1);
            newVertex2.copy(nearestPoint);
            nextEdge.point2 = newVertex2;
        }
    }

    // Update the current edge with the new vertex positions
    edge.point1.copy(newVertex1);
    edge.point2.copy(newVertex2);
    setbacks[edgeIndex] = edge;
}

/**
 * Calculates the nearest point on a line segment to another line segment
 * 
 * This function finds the intersection point between two lines in 3D space.
 * It's used to ensure that connected setback edges meet precisely at corners.
 * 
 * @param {THREE.Vector3} lineStart - Start point of the first line
 * @param {THREE.Vector3} lineEnd - End point of the first line
 * @param {THREE.Vector3} point1 - First point of the second line
 * @param {THREE.Vector3} point2 - Second point of the second line
 * @returns {THREE.Vector3} The nearest point on the first line to the second line
 */
function getNearestPointOnLineSegment(lineStart, lineEnd, point1, point2) {
    // Origin point of the first line
    const oEdge = lineStart;
    
    // Direction vector of the first line
    const dEdge = new THREE.Vector3().subVectors(lineEnd, lineStart).normalize();

    // Origin point of the second line
    const oSetback = point2;
    
    // Direction vector of the second line
    const dSetback = new THREE.Vector3().subVectors(point1, point2).normalize();

    // Difference vector between the origin points
    const diff = new THREE.Vector3().subVectors(oSetback, oEdge);

    // Cross products to find the intersection parameter
    const crossDir = new THREE.Vector3().crossVectors(dEdge, dSetback);
    const crossDirDiff = new THREE.Vector3().crossVectors(diff, dSetback);

    // Avoid division by zero if lines are parallel
    if (crossDir.length() === 0) {
        return oEdge.clone();
    }

    // Calculate the intersection parameter
    const t1 = crossDirDiff.length() / crossDir.length();

    // Calculate the intersection point using the parameter
    const intersectionPoint = new THREE.Vector3().copy(dEdge).multiplyScalar(t1).add(oEdge);

    return intersectionPoint;
}

/**
 * Updates the visual wireframe representation of the setback edges
 * 
 * This function creates or updates a wireframe mesh that visually represents
 * the setback edges in the 3D scene.
 * 
 * @param {Array} edges - Array of setback edges
 * @param {number} index - Index of the polygon in the POLYGONS array
 */
export function updateWireframe(edges, index) {
    // Collect all edge points for wireframe creation
    let edgePoints = [];
    for (let i = 0; i < edges.length; i++) {
        edgePoints.push(edges[i].point1);
        edgePoints.push(edges[i].point2);
    }
    
    // Create a material for the wireframe
    let material = MATERIALS.setbackWireframe.clone();
    
    // Create a wireframe geometry from the edge points
    let edgeWireframe = createEdgesWireframe(edgePoints, material);

    // Get the setback object for the polygon
    let setback = POLYGONS[index].setBacks;
    
    // Remove existing mesh if there is one
    if (setback.mesh) {
        removeMesh(setback);
    }
    
    // Store the new wireframe mesh
    setback.mesh = edgeWireframe;
    
    // Add the wireframe to the scene
    sceneManager.scene.add(setback.mesh);
    
    // Mark the geometry as needing an update
    if (setback.mesh.geometry && setback.mesh.geometry.attributes.position) {
        setback.mesh.geometry.attributes.position.needsUpdate = true;
    }
}

/**
 * STRICT VALIDATION:
 * Checks if all setback vertices (and their midpoints) lie inside the original segment polygon.
 * If ANY is outside, returns false.
 * 
 * @param {number} segmentIndex - Index in POLYGONS
 * @returns {boolean}
 */
export function areSetbacksInsidePolygon(segmentIndex) {
    const segmentData = POLYGONS[segmentIndex];
    const segment = segmentData.shape;
    const plane = createSegmentPlane(segmentIndex);
    const segNormal = plane.normal.clone().normalize();
    const setbacks = segmentData.setBacks.edgesData;

    const raycaster = new THREE.Raycaster();
    const dir = segNormal.clone().normalize();

    const isInside = (point) => {
        // Cast a ray from slightly above the polygon plane towards it.
        const origin = point.clone().add(dir.clone().multiplyScalar(5));
        raycaster.set(origin, dir.clone().negate());
        const intersects = raycaster.intersectObject(segment, false);
        // If the vertical ray hits the polygon, the projected point is inside the polygon surface.
        return intersects.length > 0;
    };

    for (let i = 0; i < setbacks.length; i++) {
        const e = setbacks[i];
        const p1 = e.point1;
        const p2 = e.point2;
        const mid = new THREE.Vector3().addVectors(p1, p2).divideScalar(2);

        if (!isInside(p1) || !isInside(p2) || !isInside(mid)) {
            return false;
        }
    }

    return true;
}
